<script lang="ts">
  import { AssetPaths } from '../../assets/asset-store';
  import { ABILITY_PANEL } from '../../game/constants/hud';
  import type { HudState } from '../hud-state.svelte';

  const { hud }: { hud: HudState } = $props();

  const { bar, progress } = ABILITY_PANEL;

  /**
   * Distances up from the panel's bottom edge, which sits on the viewport's bottom edge — so a
   * `yFromBottom` of -10 is simply 10 up from here. Text is bottom-anchored rather than using
   * `.hud-text`'s top-anchored correction, so the spans below shift down by the descent to put
   * the baseline, not the box edge, on the offset.
   */
  const timerBottom = -ABILITY_PANEL.timer.yFromBottom;
  const nameBottom = -ABILITY_PANEL.name.yFromBottom;
  const costBottom = -ABILITY_PANEL.cost.yFromBottom;
  const dronesBottom = -ABILITY_PANEL.drones.yFromBottom;

  // `floor(remaining/1000 + 1)`, so the last whole second reads "1" rather than "0".
  const seconds = $derived(Math.floor(hud.abilityRemainingMs / 1000 + 1));
</script>

<!-- `CooldownBar.png` to the right of the dock: the ability's name, its cooldown, and drone count. -->
<div class="ability" style:width="{bar.width}px" style:height="{bar.height}px">
  <img class="ability__backing" src={AssetPaths.ui(bar.image)} alt="" />

  <!--
    Offsets are the original's, re-expressed relative to this panel: it starts at centre+235 and its
    bottom edge is the viewport's, so centre+270 / bottom-16 becomes left 35 / bottom 10.
  -->
  <div
    class="ability__track"
    style:left="{progress.xFromCentre - bar.xFromCentre}px"
    style:bottom="{-progress.yFromBottom - progress.height}px"
    style:width="{progress.width}px"
    style:height="{progress.height}px"
  >
    <div class="ability__fill" style:width="{hud.abilityFraction * 100}%"></div>
  </div>

  <span
    class="ability__timer"
    style:bottom="{timerBottom}px"
    style:left="{ABILITY_PANEL.timer.xFromCentre - bar.xFromCentre}px"
    data-testid="ability-cooldown">{hud.abilityReady ? ABILITY_PANEL.readyLabel : seconds}</span
  >
  <span
    class="ability__name"
    style:bottom="{nameBottom}px"
    style:left="{ABILITY_PANEL.name.xFromCentre - bar.xFromCentre}px"
    data-testid="ability-name">{hud.abilityName}</span
  >

  {#if hud.abilityCost !== null}
    <span
      class="ability__cost"
      class:ability__over={!hud.abilityAffordable}
      style:bottom="{costBottom}px"
      style:left="{ABILITY_PANEL.cost.xFromCentre - bar.xFromCentre}px"
      data-testid="ability-cost">{hud.abilityCost}</span
    >
  {/if}

  {#if hud.drones > 0}
    <span
      class="ability__drones"
      style:bottom="{dronesBottom}px"
      style:left="{ABILITY_PANEL.drones.xFromCentre - bar.xFromCentre}px"
      data-testid="drones"
    >
      Drones: <span class:ability__over={hud.drones >= hud.maxDrones}
        >{hud.drones}/{hud.maxDrones}</span
      >
    </span>
  {/if}
</div>

<style>
  .ability {
    position: absolute;
    bottom: 0;
    /* The panel starts 235px right of centre, which is just past the dock's right edge. */
    left: calc(50% + 235px);
  }

  .ability__backing {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  .ability__track {
    position: absolute;
    background: rgb(17 17 17 / 30%);
  }

  .ability__fill {
    height: 100%;
    background: #465e84;
  }

  .ability__timer,
  .ability__name,
  .ability__cost,
  .ability__drones {
    position: absolute;
    line-height: 1;
    white-space: nowrap;
    color: #77eeff;
    /* Baseline correction for a bottom-anchored box; see the script block's remarks. */
    transform: translateY(0.22em);
  }

  .ability__timer {
    font-size: 12px;
  }

  .ability__name {
    font-size: 12px;
    color: #eee;
  }

  .ability__cost,
  .ability__drones {
    font-size: 10px;
  }

  .ability__over {
    color: #ee1111;
  }
</style>
